import { Alert, AlertTitle, Box, Button, Tab, Tabs } from '@mui/material';
import { ChevronLeft, Share } from '@mui/icons-material';
import {
  ComparisonView,
  DetailedView,
  PageLayout,
  ShareAudienceDialog,
  SummaryView,
  Tooltip,
} from '@/components';
import { IAudience, IAudienceInsight, IAudienceSegment } from '@/interfaces';
import React, { useEffect, useState } from 'react';
import { SHARE_AUDIENCE, useAudience } from '@/context/audience-context';
import { insightViews, insightViewsSegment } from '@/constants';

import Grid from '@mui/material/Unstable_Grid2';
import { InsightPageSkeleton } from '@/components/atoms';
import { SharedUser } from '@/types';
import { getInsight } from '@/api/insight';
import { shareAudience } from '@/api/audience';
import { useAuth0 } from '@auth0/auth0-react';
import { useHistory } from 'react-router-dom';
import { useParams } from 'react-router';
import { useSnackbar } from '@/context/snackbar-context';

export const InsightPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const history = useHistory();
  const { getAccessTokenSilently } = useAuth0();
  const { openSnackbar } = useSnackbar();
  const { state, dispatch } = useAudience();

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [hasSegments, setHasSegments] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [insight, setInsight] = useState<IAudienceInsight | null>(null);
  const [openShareAudienceDialog, setOpenShareAudienceDialog] = useState(false);
  const [selectedAudience, setSelectedAudience] = useState<IAudience | null>(
    null
  );
  const [selectedSegment, setSelectedSegment] = useState<IAudienceSegment>(
    {} as IAudienceSegment
  );
  const [selectedView, setSelectedView] = useState<string>('Summary');
  const [permissions, setPermissions] = useState<string[]>([]);
  const audienceSharedToUser =
    selectedAudience?.shared && selectedAudience?.shared_with?.length === 0;

  const canActivate = permissions.includes('social:activate');

  // check if the user can activate audiences
  useEffect(() => {
    const fetchPermissions = async () => {
      try {
        const token = await getAccessTokenSilently();
        const decodedToken: any = JSON.parse(atob(token.split('.')[1]));
        setPermissions(
          decodedToken[
            `https://${process.env.REACT_APP_AUTH0_DOMAIN}/permissions`
          ] || []
        );
      } catch (error) {
        console.error('Error fetching permissions:', error);
      }
    };

    fetchPermissions();
  }, [getAccessTokenSilently]);

  // check if user has permission to view the audience
  useEffect(() => {
    const audience = state.audiences.find((value) => value.id === Number(id));

    if (!audience) {
      setError("You do not have permission to view this audience's activation");
      return;
    } else {
      setError(null);
    }
  }, [id, state.audiences]);

  useEffect(() => {
    const fetchData = async () => {
      const accessToken = await getAccessTokenSilently();
      return await getInsight(accessToken, Number(id));
    };
    fetchData().then((response) => {
      if (response.data) {
        setInsight(response.data);
        const selectedAudience = state.audiences.find(
          (audience) => audience.id === response.data?.audience_id
        );

        if (selectedAudience) setSelectedAudience(selectedAudience);

        const audienceSegment: IAudienceSegment = {
          id: response.data.audience_id,
          summary: response.data.summary,
          score: response.data.score,
          name: 'Audience',
        };

        // if we have segments, add the audience to the beginning
        if (response.data.segments) {
          setHasSegments(true);
          response.data.segments.unshift(audienceSegment);
        }

        // Set the audience as the selected segment
        setSelectedSegment(audienceSegment);
      }

      setIsLoading(false);
      response?.error && setError(response.error.message);
    });
  }, [id, getAccessTokenSilently, state.audiences]);

  const handleShareAudience = () => {
    const selectedAudience = state.audiences.find(
      (audience) => audience.id === insight?.audience_id
    );
    if (selectedAudience) {
      setSelectedAudience(selectedAudience);
      setOpenShareAudienceDialog(true);
    }
  };

  const onBackToAudiences = () => {
    history.push(`/audience/${id}`);
  };

  const handleSegmentChange = (segment: number) => {
    const newSegment = insight?.segments.find((value) => value.id === segment);
    if (newSegment) setSelectedSegment(newSegment);
  };

  const handleShare = async (users: SharedUser[]) => {
    const accessToken = await getAccessTokenSilently();
    const { data, error } = await shareAudience(
      accessToken,
      selectedAudience?.id || 0,
      users
    );

    if (data) {
      openSnackbar(data.message, 'success');

      dispatch({
        type: SHARE_AUDIENCE,
        payload: { audienceId: selectedAudience?.id || 0, users: users },
      });

      setOpenShareAudienceDialog(false);
    }

    if (error) {
      openSnackbar(error.message, 'error');
      return;
    }
  };

  const renderInsightView = () => {
    if (!insight) return null;

    switch (selectedView) {
      case 'Summary':
        return (
          <Grid xs={12}>
            <SummaryView
              insight={insight}
              selectedSegment={selectedSegment}
              onSegmentChange={handleSegmentChange}
            />
          </Grid>
        );
      case 'Detailed':
        return (
          <DetailedView
            insight={insight}
            selectedSegment={selectedSegment}
            onSegmentChange={handleSegmentChange}
            error={error}
          />
        );
      case 'Comparison':
        return (
          <Grid xs={12}>
            <ComparisonView segments={insight.segments} />
          </Grid>
        );
      default:
        return null;
    }
  };

  return (
    <PageLayout>
      <Grid
        container
        spacing={2}
        px={1}
        justifyContent="center"
        alignItems="center"
      >
        {isLoading ? (
          <InsightPageSkeleton />
        ) : error ? (
          <Grid xs={12}>
            <Alert severity="error">
              <AlertTitle>{error}</AlertTitle>
            </Alert>
          </Grid>
        ) : (
          insight && (
            <>
              <Grid xs={2}>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<ChevronLeft />}
                  onClick={onBackToAudiences}
                >
                  Back to Audience
                </Button>
              </Grid>
              <Grid xs={10}>
                <Box
                  display="flex"
                  flexDirection="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Tabs
                    value={selectedView}
                    onChange={(_event, newValue) => setSelectedView(newValue)}
                    variant="scrollable"
                    scrollButtons="auto"
                  >
                    {(hasSegments ? insightViewsSegment : insightViews).map(
                      (view, index) => {
                        return <Tab key={index} value={view} label={view} />;
                      }
                    )}
                  </Tabs>
                  <Grid display={'flex'} flexDirection={'row'} gap={3}>
                    <Tooltip
                      placement="bottom"
                      title="Share the audience with other members of your team"
                    >
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={handleShareAudience}
                        disabled={audienceSharedToUser}
                      >
                        <Share fontSize="small" sx={{ mr: 0.5 }} />
                        Share
                      </Button>
                    </Tooltip>
                    {canActivate && (
                      <Tooltip
                        placement="bottom"
                        title="Build an activation criteria for different social media platforms"
                      >
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => {
                            history.push(`/audience/${id}/activation`);
                          }}
                        >
                          Activate Audience
                        </Button>
                      </Tooltip>
                    )}
                  </Grid>
                </Box>
              </Grid>
              {renderInsightView()}
            </>
          )
        )}
      </Grid>
      {selectedAudience && (
        <ShareAudienceDialog
          selectedAudience={selectedAudience}
          open={openShareAudienceDialog}
          onClose={() => setOpenShareAudienceDialog(false)}
          onShare={handleShare}
        />
      )}
    </PageLayout>
  );
};
